<script setup>
import {computed, ref, watch} from "vue";

const emit = defineEmits('click')

const props = defineProps({
  results: {
    type: Object,
    required: true
  }
})

// freight offers cheapest first; parcel rates keep the order they came in. The fixed
// $0.00 choices (a quote-always-zero service: pickup, our truck, ...) always last
const amount = (r) => Number(String(r).replace(/,/g, '')) || 0
const fixedZero = (q) => !q.freight_offer && amount(q.rate) === 0
// freight comes back insured and uninsured when there is a value to insure: one set at a
// time, starting on what the record asks for
const insured = (q) => !!(q.freight_offer && q.freight_offer.insured_amount)
const insurance = computed(() => (props.results && props.results.insurance) || null)
const hasInsured = computed(() => ((props.results && props.results.results) || []).some(insured))
const hasFreight = computed(() => ((props.results && props.results.results) || []).some(q => q.freight_offer))
const showInsured = ref(true)
watch(() => props.results, () => {
  showInsured.value = hasInsured.value && !!(insurance.value ? insurance.value.requested : true)
}, {immediate: true})

const sorted = computed(() => {
  const all = (props.results && props.results.results) || []
  const list = hasInsured.value ? all.filter(q => !q.freight_offer || insured(q) === showInsured.value) : all
  const rated = list.filter(q => !fixedZero(q))
  const zero = list.filter(fixedZero)
  if (rated.some(q => q.freight_offer))
    rated.sort((a, b) => amount(a.rate) - amount(b.rate))
  return [...rated, ...zero]
})

const validUntil = (offer) => offer && offer.expires
    ? new Date(offer.expires).toLocaleString([], {weekday: 'short', hour: 'numeric', minute: '2-digit'})
    : null
</script>

<template>
  <div v-if="results">
    <b-alert variant="warning" show size="sm" v-if="results && results.errors && results.errors.length">
      <ul class="m-0 p-0">
        <li v-for="error in results.errors" :key="error" v-text="error"/>
      </ul>
    </b-alert>
    <b-alert show variant="warning" class="small" v-if="!hasInsured && insurance && insurance.requested && hasFreight">
      {{ insurance.by || 'This shipment' }} is requesting insurance<template v-if="insurance.value"> ({{ insurance.value | money }})</template>,
      but no carrier quoted it insured: these offers are uninsured.
    </b-alert>
    <div class="d-flex flex-wrap align-items-center mb-2" v-if="hasInsured">
      <b-form-radio-group v-model="showInsured" class="mr-3"
                          :options="[{value: true, text: 'Insurance'}, {value: false, text: 'No Insurance'}]"/>
      <small :class="insurance && insurance.requested ? 'text-success' : 'text-muted'" v-if="insurance">
        {{ insurance.by || 'This shipment' }} {{ insurance.requested ? 'is' : 'is not' }} requesting insurance<template
          v-if="insurance.requested && insurance.value"> ({{ insurance.value | money }})</template>
      </small>
    </div>
    <b-list-group v-if="results">
      <b-list-group-item :variant="ship.failsDeliverBy ? 'warning' : ''" v-for="(ship,idx) in sorted"
                         :key="idx" href="#" @click="emit('click', ship)">
        <div class="d-flex">
          <h6 class="mr-auto">
            <template v-if="ship.freight_offer">{{ ship.description }}</template>
            <template v-else>{{ ship.carrier }} - {{ ship.description }}</template>
          </h6>
          <h6>{{ ship.rate | money }}</h6>
        </div>
        <div class="d-flex">
          <div class="font-italic mr-auto">
            Pickup <strong>{{ ship.shipDate_day }}</strong>
          </div>
          <div class="font-italic">
            Arrives <strong>{{ ship.deliverBy_day }}</strong>
          </div>
        </div>
        <!-- a freight offer: the line, how long, and how long the price holds -->
        <div class="d-flex small text-muted" v-if="ship.freight_offer">
          <div class="mr-auto">
            {{ ship.freight_offer.scac }}
            <template v-if="ship.freight_offer.transit_days"> · {{ ship.freight_offer.transit_days }} day transit</template>
            <template v-if="ship.freight_offer.service_type === 'Interline'"> · interline</template>
            <b-badge variant="success" class="ml-1" v-if="ship.freight_offer.guaranteed">Guaranteed</b-badge>
            <b-badge variant="info" class="ml-1" v-if="ship.freight_offer.insured_amount">Insured {{ ship.freight_offer.insured_amount | money }}<template v-if="ship.freight_offer.insurance_premium"> ({{ ship.freight_offer.insurance_premium | money }} incl.)</template></b-badge>
          </div>
          <div v-if="validUntil(ship.freight_offer)">valid until {{ validUntil(ship.freight_offer) }}</div>
        </div>
        <div class="font-weight-bold text-center" v-if="ship.failsDeliverBy">
          ** LATE **
        </div>
      </b-list-group-item>
    </b-list-group>
  </div>
</template>

<style scoped>

</style>
